import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import type { ReviewComment } from "@/components/ai/review/types";
import { useReviewComments, formatReviewCommentsForAgent } from "@/components/ai/review/use-review-comments";
import { GitPullRequest, Send } from "lucide-react";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { Agents, useCreateAgentExecution, useSendToAgentExecution } from "@/queries/agent-queries";
import { AgentCodeContext } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useEnvironment, useStartEnvironment } from "@/queries/environment-queries";
import { FileChangeList, type FileChangeListRef } from "@/routes/environments/code-changes/FileChangeList";
import { showStart } from "@/routes/environments/phase";
import {
    AgentExecution_Phase,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { EnvironmentGitStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { pluralizeWithCount } from "@/utils/strings";

export type CodeChangesPanelRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
};

export type CodeChangesPanelProps = {
    environmentId: string;
    enableReviewComments?: boolean;
};

export const CodeChangesPanel = forwardRef<CodeChangesPanelRef, CodeChangesPanelProps>(
    ({ environmentId, enableReviewComments = false }, ref) => {
        const { data: environment } = useEnvironment(environmentId);
        const { mutate: startEnvironment, isPending: isStarting } = useStartEnvironment();
        const [openedFiles, setOpenedFiles] = useState<string[]>([]);
        const fileChangeListRef = useRef<FileChangeListRef>(null);
        const stickyHeaderRef = useRef<HTMLDivElement>(null);
        const sentinelRef = useRef<HTMLDivElement>(null);
        const [isHeaderStuck, setIsHeaderStuck] = useState(false);

        // Detect when sticky header becomes stuck using IntersectionObserver
        useEffect(() => {
            const sentinel = sentinelRef.current;
            if (!sentinel) return;

            // Find the scroll container (ScrollArea viewport)
            const scrollContainer = sentinel.closest("[data-radix-scroll-area-viewport]");

            const observer = new IntersectionObserver(
                ([entry]) => {
                    // Header is stuck when sentinel is not visible (scrolled past)
                    setIsHeaderStuck(!entry.isIntersecting);
                },
                {
                    root: scrollContainer,
                    threshold: 0,
                },
            );

            observer.observe(sentinel);

            return () => {
                observer.disconnect();
            };
        }, []);

        const { environmentConversation } = useEnvironmentConversation(environmentId);
        const agentExecution = getAgentExecution(environmentConversation);
        const createAgentExecution = useCreateAgentExecution();
        const sendToAgentExecution = useSendToAgentExecution();

        // Get conversation messages to extract code annotations
        const { pendingMessages } = useOptimisticPendingMessages();
        const { messages } = useStreamConversation(
            agentExecution?.id,
            agentExecution?.status?.conversationUrl,
            pendingMessages,
        );

        const {
            comments,
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            isAnnotationDismissed,
            setCommentThumbsDown,
        } = useReviewComments(environmentId);

        // Track processed annotations to avoid re-running effect when comments change
        const processedAnnotationsRef = useRef<Set<string>>(new Set());
        // Keep refs to avoid re-running effect when these change
        const commentsRef = useRef(comments);
        commentsRef.current = comments;
        const openedFilesRef = useRef(openedFiles);
        openedFilesRef.current = openedFiles;

        // Convert agent code annotations to review comments (only when feature flag is enabled)
        useEffect(() => {
            console.log("Processing agent annotations effect triggered");
            if (!enableReviewComments || !messages || !environmentId) return;
            console.log("Processing messages:", messages.length);

            const filesToOpen: string[] = [];

            // Build a set of existing annotation IDs from comments to avoid duplicates
            const existingAnnotationIds = new Set(
                commentsRef.current.filter((c) => c.annotationId).map((c) => c.annotationId),
            );

            for (const message of messages) {
                if (message.type !== "agent") continue;
                for (const fragment of message.fragments) {
                    if (fragment.type === "codeAnnotation") {
                        const annotation = fragment;

                        // Skip if already processed in this session
                        if (processedAnnotationsRef.current.has(annotation.id)) continue;

                        // Skip if this annotation was previously dismissed by the user
                        if (isAnnotationDismissed(annotation.id)) {
                            console.log("dismissed by user, skipping");
                            processedAnnotationsRef.current.add(annotation.id);
                            continue;
                        }

                        // Skip if a comment with this annotation ID already exists (loaded from storage)
                        if (existingAnnotationIds.has(annotation.id)) {
                            console.log("processed already, skipping");
                            processedAnnotationsRef.current.add(annotation.id);
                            continue;
                        }

                        // Mark as processed before adding to prevent re-processing
                        processedAnnotationsRef.current.add(annotation.id);
                        addComment(
                            annotation.filePath,
                            annotation.startLine,
                            annotation.endLine,
                            annotation.description,
                            "agent",
                            annotation.id,
                        );

                        // Track file to open if not already in the list
                        if (
                            !filesToOpen.includes(annotation.filePath) &&
                            !openedFilesRef.current.includes(annotation.filePath)
                        ) {
                            filesToOpen.push(annotation.filePath);
                        }
                    }
                }
            }

            // Open files with new comments
            if (filesToOpen.length > 0) {
                setOpenedFiles((prev) => [...prev, ...filesToOpen]);
            }
        }, [enableReviewComments, messages, environmentId, addComment, isAnnotationDismissed]);

        const [isReviewLoading, setIsReviewLoading] = useState(false);
        const [isCreatePRLoading, setIsCreatePRLoading] = useState(false);

        // Prevent race condition: disable both buttons when any operation is in progress
        const isAnyOperationLoading = isReviewLoading || isCreatePRLoading || createAgentExecution.isPending;

        const gitStatus = environment?.status?.content?.git;
        const hasChanges = Boolean(gitStatus?.changedFiles.length);
        const shouldShowStartEnv = environment ? showStart(environment) : false;

        // Filter comments to only include those for files in the current change list
        const changedFilePaths = useMemo(
            () => new Set(gitStatus?.changedFiles.map((f) => f.path) ?? []),
            [gitStatus?.changedFiles],
        );
        const commentsInChangeList = useMemo(
            () => comments.filter((c) => changedFilePaths.has(c.filePath)),
            [comments, changedFilePaths],
        );

        // Count of comments that will be submitted (excluding thumbsDown)
        const submittableCommentsCount = useMemo(
            () => commentsInChangeList.filter((c) => !c.thumbsDown).length,
            [commentsInChangeList],
        );

        const isAgentBusy =
            agentExecution?.status?.phase === AgentExecution_Phase.RUNNING ||
            agentExecution?.status?.phase === AgentExecution_Phase.PENDING;

        useImperativeHandle(
            ref,
            () => ({
                scrollToFile: (filePath: string) => {
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
                openFile: (filePath: string) => {
                    // Open the file in the accordion
                    if (!openedFiles.includes(filePath)) {
                        setOpenedFiles((prev) => [...prev, filePath]);
                    }
                    // Then scroll to it
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
            }),
            [openedFiles],
        );

        const handleStartEnvironment = () => {
            if (environment) {
                startEnvironment({ environmentId: environment.id });
            }
        };

        const handleAddComment = useCallback(
            (
                filePath: string,
                startLine: number,
                endLine: number,
                content: string,
                side?: "deletions" | "additions",
                contentHash?: string,
            ) => {
                addComment(filePath, startLine, endLine, content, "user", undefined, side, contentHash);
            },
            [addComment],
        );

        const handleUpdateComment = useCallback(
            (reviewComment: ReviewComment, newContent: string) => {
                updateComment(reviewComment.id, newContent);
            },
            [updateComment],
        );

        const handleDeleteComment = useCallback(
            (reviewComment: ReviewComment) => {
                removeComment(reviewComment.id);
            },
            [removeComment],
        );

        const handleSetCommentThumbsDown = useCallback(
            (reviewComment: ReviewComment, thumbsDown: boolean) => {
                setCommentThumbsDown(reviewComment.id, thumbsDown);
            },
            [setCommentThumbsDown],
        );

        const [isSubmitCommentsLoading, setIsSubmitCommentsLoading] = useState(false);

        const handleSubmitComments = useCallback(async () => {
            // Filter out thumbsDown comments - they should be skipped
            const commentsToSubmit = commentsInChangeList.filter((c) => !c.thumbsDown);
            if (!agentExecution?.id || commentsToSubmit.length === 0) return;

            setIsSubmitCommentsLoading(true);
            try {
                const formattedComments = formatReviewCommentsForAgent(commentsToSubmit);

                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecution.id,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: formattedComments,
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );
                // Clear comments after successful submission
                clearAllComments();
            } catch (error) {
                console.error("Failed to submit comments:", error);
            } finally {
                setIsSubmitCommentsLoading(false);
            }
        }, [agentExecution?.id, commentsInChangeList, sendToAgentExecution, clearAllComments]);

        const handleStartReview = useCallback(async () => {
            if (!gitStatus?.changedFiles.length) return;

            setIsReviewLoading(true);
            try {
                const fileList = gitStatus.changedFiles.map((f) => `- ${f.path}`).join("\n");
                const reviewCommand = `/ona:review\n\nFiles changed:\n${fileList}`;

                let executionId = agentExecution?.id;

                // Create agent execution if none exists
                if (!executionId) {
                    const { agentExecutionId } = await createAgentExecution.mutateAsync({
                        agentId: Agents.InEnvironmentOna.id,
                        codeContext: new AgentCodeContext({
                            context: {
                                case: "environmentId",
                                value: environmentId,
                            },
                        }),
                    });
                    executionId = agentExecutionId;
                }

                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: executionId,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: reviewCommand,
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );
            } catch (error) {
                console.error("Failed to start review:", error);
            } finally {
                setIsReviewLoading(false);
            }
        }, [agentExecution?.id, gitStatus?.changedFiles, sendToAgentExecution, createAgentExecution, environmentId]);

        const handleCreatePullRequest = useCallback(async () => {
            setIsCreatePRLoading(true);
            try {
                let executionId = agentExecution?.id;

                // Create agent execution if none exists
                if (!executionId) {
                    const { agentExecutionId } = await createAgentExecution.mutateAsync({
                        agentId: Agents.InEnvironmentOna.id,
                        codeContext: new AgentCodeContext({
                            context: {
                                case: "environmentId",
                                value: environmentId,
                            },
                        }),
                    });
                    executionId = agentExecutionId;
                }

                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: executionId,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({
                                            content: "Create a pull request for the current changes.",
                                        }),
                                    },
                                }),
                            ],
                        }),
                    }),
                );
            } catch (error) {
                console.error("Failed to create pull request:", error);
            } finally {
                setIsCreatePRLoading(false);
            }
        }, [agentExecution?.id, sendToAgentExecution, createAgentExecution, environmentId]);

        if (!environment) {
            return null;
        }

        return (
            <div>
                {/* Sentinel for detecting when sticky header becomes stuck */}
                {enableReviewComments && <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />}
                {/* Sticky header: negative top/margins offset the ScrollArea padding (p-4 md:p-6) in EnvironmentDetailsLayout.tsx so header sticks at viewport edge */}
                <div
                    ref={enableReviewComments ? stickyHeaderRef : undefined}
                    className={cn(
                        "flex items-center justify-between py-2.5",
                        enableReviewComments &&
                            "sticky -top-4 z-10 -mx-4 w-[calc(100%+2rem)] bg-surface-primary px-4 md:-top-6 md:-mx-6 md:w-[calc(100%+3rem)] md:px-6",
                        enableReviewComments && isHeaderStuck && "border-b border-border-subtle",
                    )}
                >
                    <span className="mr-2 select-none text-start text-md font-medium">Code Changes</span>
                    {enableReviewComments && hasChanges && (
                        <div className="flex items-center gap-2">
                            {commentsInChangeList.length > 0 && (
                                <div className="flex items-center">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSubmitComments}
                                        disabled={
                                            isSubmitCommentsLoading || isAgentBusy || submittableCommentsCount === 0
                                        }
                                        className="rounded-r-none"
                                        data-tracking-id="send-comments-to-ona"
                                    >
                                        <Send size={16} className="shrink-0" />
                                        <span>
                                            {isSubmitCommentsLoading
                                                ? "Sending..."
                                                : `Send ${pluralizeWithCount(submittableCommentsCount, false, "comment")} to Ona`}
                                        </span>
                                    </Button>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className="rounded-l-none border-l border-white/20 px-1.5"
                                                disabled={isSubmitCommentsLoading || isAgentBusy}
                                                data-tracking-id="send-comments-dropdown"
                                            >
                                                <IconChevronDownSmall size="sm" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                                variant="destructive"
                                                onClick={clearAllComments}
                                                data-tracking-id="dismiss-all-comments"
                                            >
                                                Dismiss all comments
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            )}
                            {gitStatus?.changedFiles.length && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleStartReview}
                                    disabled={isAnyOperationLoading || isAgentBusy}
                                    data-tracking-id="start-review-code-changes"
                                >
                                    {isReviewLoading ? "Reviewing..." : "Review"}
                                </Button>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleCreatePullRequest}
                                disabled={isAnyOperationLoading || isAgentBusy}
                                data-tracking-id="create-pull-request"
                            >
                                <GitPullRequest size={16} className="shrink-0" />
                                <span>{isCreatePRLoading ? "Creating..." : "Create PR"}</span>
                            </Button>
                        </div>
                    )}
                </div>
                {shouldShowStartEnv && hasChanges && (
                    <Banner
                        className="mb-2"
                        variant="info"
                        text="Start the environment to view code changes."
                        action={{
                            text: "Start environment",
                            onClick: handleStartEnvironment,
                            loading: isStarting,
                            "data-tracking-id": "start-environment-code-changes-panel",
                        }}
                    />
                )}
                {hasChanges ? (
                    <FileChangeList
                        ref={fileChangeListRef}
                        environment={environment}
                        gitStatus={new EnvironmentGitStatus(gitStatus)}
                        openedFiles={openedFiles}
                        onOpenedFilesChange={setOpenedFiles}
                        commentsByFile={enableReviewComments ? commentsByFile : undefined}
                        onAddComment={enableReviewComments ? handleAddComment : undefined}
                        onUpdateComment={enableReviewComments ? handleUpdateComment : undefined}
                        onDeleteComment={enableReviewComments ? handleDeleteComment : undefined}
                        onSetCommentThumbsDown={enableReviewComments ? handleSetCommentThumbsDown : undefined}
                    />
                ) : (
                    <Text className="text-sm text-content-secondary">No uncommitted changes</Text>
                )}
            </div>
        );
    },
);
CodeChangesPanel.displayName = "CodeChangesPanel";
